import { useState } from 'react';
import { receptionApi, type ChangeSetView, type QueueBackupView } from '../api/client';
import { prettyNumber } from '../lib/calls';
import { useData } from '../lib/useData';
import { ReviewDrawer } from './ReviewDrawer';
import { Button, Card, LoadError, Loading, Notice } from './ui';

const SECONDS = [30, 45, 60, 90, 120, 180, 300];

/**
 * Queue backup (1.13.0, Teampace 2714) in Settings → Reception: for each call queue, callers who
 * wait longer than a set time go to the receptionists (the Operators group, or the only
 * receptionist). It is the queue's own "longest wait, then send callers to", so each change is a
 * change set to review, read back when it's applied.
 */
export function QueueBackupCard({ domain, writable }: { domain: string; writable: boolean }) {
  const data = useData(() => receptionApi.queueBackups(domain), [domain]);
  const [seconds, setSeconds] = useState<Record<string, number>>({});
  const [plan, setPlan] = useState<ChangeSetView | null>(null);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);

  if (data.error) {
    return (
      <Card title="Queue backup">
        <div className="card-body">
          <LoadError error={data.error} retry={data.reload} />
        </div>
      </Card>
    );
  }
  if (!data.data) {
    return (
      <Card title="Queue backup">
        <div className="card-body">
          <Loading label="Loading queue backup" />
        </div>
      </Card>
    );
  }
  const { target, queues } = data.data;
  if (!queues.length) return null;
  const secsOf = (q: QueueBackupView) => seconds[q.extension] ?? q.seconds ?? 60;

  async function change(q: QueueBackupView, on: boolean) {
    setBusy(q.extension);
    setNote(null);
    try {
      const r = await receptionApi.setQueueBackup(domain, q.extension, on, secsOf(q));
      if (r.changeSet) setPlan(r.changeSet);
      else setNote({ tone: 'ok', text: `${q.name} is already set that way.` });
    } catch (err) {
      setNote({ tone: 'crit', text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy('');
    }
  }

  return (
    <Card title="Queue backup">
      <div className="card-body settings-section">
        <p className="muted settings-lede">
          {target
            ? `When a caller waits too long in a queue, send them to ${target.name} (${target.extension}). They leave the queue and ring the receptionists.`
            : 'Make someone a receptionist first (on their page in People). With two or more, Stitch makes the Operators group, and the backup rings all of them.'}
        </p>
        <ul className="queue-backup">
          {queues.map((q) => (
            <li key={q.extension}>
              <span className="cell-stack grow">
                <strong>
                  {q.name} · {q.extension}
                </strong>
                <small className="muted">
                  {q.on
                    ? `Backed up after ${q.seconds} seconds`
                    : q.elsewhere
                      ? `Callers who wait ${q.seconds ? `over ${q.seconds} seconds` : 'too long'} go to ${q.elsewhereName || prettyNumber(q.elsewhere)} now. Backup sends them to the receptionists instead; turning it off puts this back.`
                      : 'No backup'}
                </small>
              </span>
              <label className="queue-backup-after">
                <span className="muted">After</span>
                <select
                  value={secsOf(q)}
                  disabled={!writable || !target || Boolean(busy)}
                  onChange={(e) => setSeconds((s) => ({ ...s, [q.extension]: Number(e.target.value) }))}
                  aria-label={`Seconds before ${q.name}’s callers go to the receptionists`}
                >
                  {[...new Set([...SECONDS, secsOf(q)])]
                    .sort((a, b) => a - b)
                    .map((n) => (
                      <option key={n} value={n}>
                        {n < 120 ? `${n} seconds` : `${n / 60} minutes`}
                      </option>
                    ))}
                </select>
              </label>
              {writable && (
                <span className="queue-backup-actions">
                  {q.on && secsOf(q) !== q.seconds && (
                    <Button size="small" disabled={Boolean(busy)} onClick={() => void change(q, true)}>
                      Change
                    </Button>
                  )}
                  <Button size="small" variant={q.on ? 'ghost' : 'secondary'} disabled={Boolean(busy) || (!q.on && !target)} onClick={() => void change(q, !q.on)} aria-label={`${q.on ? 'Turn off' : 'Turn on'} backup for ${q.name}`}>
                    {q.on ? 'Turn off' : 'Turn on'}
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
        {note && <Notice tone={note.tone}>{note.text}</Notice>}
      </div>
      {plan && (
        <ReviewDrawer
          open
          tenant={domain}
          items={[]}
          planned={plan}
          onUnstage={() => undefined}
          onClose={() => {
            setPlan(null);
            data.reload();
          }}
        />
      )}
    </Card>
  );
}
